/**
 * v-permission 指令
 * 无权限则移除元素或设置 disabled
 * 支持权限更新事件自动刷新
 *
 * 使用方式：
 * v-permission="'wallpaper:add'"           单个权限
 * v-permission="['wallpaper:add', 'wallpaper:edit']" 多个权限（任一即可）
 * v-permission:all="['perm1', 'perm2']"    多个权限（全部需要）
 * v-permission:role="'editor'"            角色检查
 * v-permission:role="['editor', 'reviewer']" 角色检查（任一即可）
 */
import { hasPermission, hasAnyPermission, hasAllPermissions, hasRole, hasAnyRole } from '../utils/permission'

/**
 * 检查权限并处理元素
 * @param {HTMLElement} el - DOM 元素
 * @param {object} binding - 指令绑定
 */
function checkPermission(el, binding) {
  const { value, arg, modifiers } = binding

  // 角色检查
  if (arg === 'role') {
    if (Array.isArray(value)) {
      if (!hasAnyRole(value)) {
        hideOrDisable(el, modifiers)
      } else {
        showElement(el)
      }
    } else {
      if (!hasRole(value)) {
        hideOrDisable(el, modifiers)
      } else {
        showElement(el)
      }
    }
    return
  }

  // 权限检查
  if (Array.isArray(value)) {
    if (modifiers.all) {
      // 需要所有权限
      if (!hasAllPermissions(value)) {
        hideOrDisable(el, modifiers)
      } else {
        showElement(el)
      }
    } else {
      // 任一权限即可
      if (!hasAnyPermission(value)) {
        hideOrDisable(el, modifiers)
      } else {
        showElement(el)
      }
    }
  } else {
    if (!hasPermission(value)) {
      hideOrDisable(el, modifiers)
    } else {
      showElement(el)
    }
  }
}

/**
 * 隐藏或禁用元素（不直接移除，便于权限更新时恢复）
 * @param {HTMLElement} el - DOM 元素
 * @param {object} modifiers - 修饰符
 */
function hideOrDisable(el, modifiers) {
  if (modifiers.disable) {
    // 禁用模式：设置 disabled 属性
    el.setAttribute('disabled', 'disabled')
    el.classList.add('is-disabled')
    el.style.pointerEvents = 'none'
    el.style.opacity = '0.5'
  } else {
    // 默认模式：隐藏元素（不直接移除）
    el.style.display = 'none'
    el.setAttribute('data-permission-hidden', 'true')
  }
}

/**
 * 显示元素
 * @param {HTMLElement} el - DOM 元素
 */
function showElement(el) {
  if (el.getAttribute('data-permission-hidden') === 'true') {
    el.removeAttribute('data-permission-hidden')
    el.style.display = ''
  }
  if (el.hasAttribute('disabled')) {
    el.removeAttribute('disabled')
    el.classList.remove('is-disabled')
    el.style.pointerEvents = ''
    el.style.opacity = ''
  }
}

export default {
  bind(el, binding) {
    checkPermission(el, binding)
    el._permissionBinding = binding

    el._onPermissionsUpdated = () => {
      if (el._permissionBinding) {
        checkPermission(el, el._permissionBinding)
      }
    }
    window.addEventListener('permissions-updated', el._onPermissionsUpdated)
  },
  update(el, binding) {
    el._permissionBinding = binding
    checkPermission(el, binding)
  },
  unbind(el) {
    if (el._onPermissionsUpdated) {
      window.removeEventListener('permissions-updated', el._onPermissionsUpdated)
      el._onPermissionsUpdated = null
    }
  }
}
